import 'twin.macro'

import { Ring } from '@uiball/loaders'
import ContainerWithSteps from 'components/UI/Container/ContainerWithSteps'
import DecisionHeader from 'components/views/Decision/DecisionHeader'
import LoanConfirmation from 'components/views/Decision/LoanConfirmation'
import LoanCorpCardOpening from 'components/views/Decision/LoanCorpCardOpening'
import LoanProgramSelection from 'components/views/Decision/LoanProgramSelection'
import LoanSubViewRenderer from 'components/views/Decision/LoanSubViewRenderer'
import { useAppSelector } from 'hooks'
import { useCreditDecision } from 'hooks/useCreditDecision'
import { useImprovePledgeFlowStatus } from 'hooks/useImprovePledgeFlowStatus'
import { useThirdPartyPledgerStatus } from 'hooks/useThirdPartyPledgerStatus'
import { useCallback, useEffect, useMemo } from 'react'
import { useSetThirdPartyPledgeDecisionMutation } from 'redux/api/3rdPledgerApi'
import { useSetApplicationDecisionMutation, useSetApplicationPledgeDecitionMutation } from 'redux/api/scoringApi'
import { store } from 'redux/store'
import {
  loanParamsSelector,
  mainViewSelector,
  okedIDSelector,
  propertySelector
} from 'redux/store/slices/decisionSlice'
import { getLoanInformation } from 'redux/store/slices/loanSlice'
import { getStatusSelector } from 'redux/store/slices/statusSlice'
import { getActiveCreditApplication, getCurrentFlowId } from 'redux/store/slices/userSlice'
import { ApplicationEvents } from 'share/enums/events'
import { FLOW_STEPS } from 'share/types'
import type { ScoringDefinedCreditProgramV1 } from 'share/types/api/credits'
import { SetApplicationDecisionPayload, SetApplicationPledgeDecisionPayload } from 'share/types/api/main'
import Emitter from 'utils/emitter'
import { isScoringOptionsArray, isScoringOptionsObject } from 'utils/type-guards'

import { DecisionViews } from './constants'

const DecisionPage = () => {
  // Selectors
  const mainView = useAppSelector(mainViewSelector)
  const property = useAppSelector(propertySelector)
  const { loanRepaymentMethod } = useAppSelector(getLoanInformation)
  const { main_flow_id } = useAppSelector(getActiveCreditApplication)
  const currentFlowId = useAppSelector(getCurrentFlowId)
  const { current_step } = useAppSelector(getStatusSelector)

  // Custom hooks
  const { selectedSubProgram, is3rdPartyPledgerApplied, programs, status, prepareLoanParamsView, prepareSubView } =
    useCreditDecision()
  const { isPledgerConfirmationFlowDone } = useThirdPartyPledgerStatus()
  const { isApprovePropertyPledgesStepInProgress } = useImprovePledgeFlowStatus()

  // Mutations
  const [setDecision, { isLoading: isSetDecisionLoading }] = useSetApplicationDecisionMutation()
  const [setPledgeDecition, { isLoading: isSetPledgeDecisionLoading }] = useSetApplicationPledgeDecitionMutation()
  const [setThirdPartyPledgeDecision, { isLoading: isSetThirdPartyPledgeDecisionLoading }] =
    useSetThirdPartyPledgeDecisionMutation()

  const handleSetApplicationDecision = useCallback(async () => {
    try {
      const _state = store.getState()
      const { amount, period } = loanParamsSelector(_state)
      const oked = okedIDSelector(_state)

      if (!selectedSubProgram || !currentFlowId || !main_flow_id || !loanRepaymentMethod || !amount || !oked || !period)
        throw new Error('Some of the required values are not defined!')
      let option: ScoringDefinedCreditProgramV1['options'] | undefined
      if (isScoringOptionsArray(selectedSubProgram)) {
        option = selectedSubProgram.options.find(option => option.credit_params.period === +period)
      } else if (isScoringOptionsObject(selectedSubProgram)) {
        option = selectedSubProgram.options
      }

      if (!option) throw new Error('Option not found!')

      let body: SetApplicationDecisionPayload | SetApplicationPledgeDecisionPayload = {
        id: current_step?.flow_type === 'IMPROVE_PLEDGE_CREDIT_CONDITIONS' ? currentFlowId : main_flow_id.id,
        amount: +amount,
        oked,
        period: +period,
        repayment_method: loanRepaymentMethod,
        option: option.id,
        program: selectedSubProgram.id
      }
      if (selectedSubProgram.with_pledges && property) {
        body = {
          ...body,
          property_pledge: property.id
        }
        if (property.programs.by_third_party_pledger) {
          body = {
            ...body,
            id: currentFlowId
          }
          await setThirdPartyPledgeDecision(body).unwrap()
        } else {
          await setPledgeDecition(body).unwrap()
        }
      } else {
        await setDecision(body).unwrap()
      }

      Emitter.emit(ApplicationEvents.FETCH_FLOW_IDS_AND_NAVIGATE)
    } catch (err) {
      console.log(err)
    }
  }, [
    currentFlowId,
    current_step?.flow_type,
    loanRepaymentMethod,
    main_flow_id,
    property,
    selectedSubProgram,
    setDecision,
    setPledgeDecition,
    setThirdPartyPledgeDecision
  ])

  const views = useMemo(() => {
    if (!mainView) throw new Error('mainView is not defined')
    return {
      [DecisionViews.PROGRAMS]: <LoanProgramSelection />,
      [DecisionViews['LOAN-CONDITIONS']]: <LoanSubViewRenderer />,
      [DecisionViews['LOAN-CONFIRMATION']]: (
        <LoanConfirmation
          isLoading={isSetDecisionLoading || isSetPledgeDecisionLoading || isSetThirdPartyPledgeDecisionLoading}
          onSetDecision={handleSetApplicationDecision}
        />
      ),
      [DecisionViews['CHOOSE-DELIVERY-ADDRESS']]: (
        <LoanCorpCardOpening
          isLoading={isSetDecisionLoading || isSetPledgeDecisionLoading || isSetThirdPartyPledgeDecisionLoading}
          onSetDecision={handleSetApplicationDecision}
        />
      )
    }[mainView]
  }, [
    handleSetApplicationDecision,
    isSetDecisionLoading,
    isSetPledgeDecisionLoading,
    isSetThirdPartyPledgeDecisionLoading,
    mainView
  ])

  useEffect(() => {
    if (!programs.isSuccess || !status.isSuccess) return
    const { current_step } = status.data
    if (!current_step) return
    const { flow_type, flow_status, step, status: stepStatus } = current_step
    if (step === FLOW_STEPS.APPROVE_PROPERTY_PLEDGES && stepStatus === 'FAILED') return
    if (
      (flow_type === 'IMPROVE_PLEDGE_CREDIT_CONDITIONS' || flow_type === 'CONFIRMATION_THIRD_PARTY_PLEDGE') &&
      flow_status === 'RETRY'
    ) {
      const ownProgram = programs.data.find(p => p.program_code === 'OWN_PROGRAM')
      if (!ownProgram) return
      prepareLoanParamsView(ownProgram.program_code)
      const programWithPledge = ownProgram.programs.find(p => p.with_pledges)
      if (!programWithPledge) return
      prepareSubView(programWithPledge.id)
    }
  }, [prepareLoanParamsView, prepareSubView, programs.data, programs.isSuccess, status.data, status.isSuccess])

  if (programs.isLoading || status.isLoading) {
    return (
      <ContainerWithSteps>
        <div tw='mt-4 md:mt-0 flex items-center justify-center'>
          <Ring color='var(--primary-color)' size={64} />
        </div>
      </ContainerWithSteps>
    )
  }
  return (
    <ContainerWithSteps>
      <fieldset
        disabled={
          (is3rdPartyPledgerApplied && !isPledgerConfirmationFlowDone) || isApprovePropertyPledgesStepInProgress
        }
        tw='space-y-6'
      >
        <DecisionHeader />
        {views}
      </fieldset>
    </ContainerWithSteps>
  )
}

export default DecisionPage
